import { useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, setDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

export const MAX_FOLLOWS = 50;

// One shared Firestore listener for the whole app (many FollowButtons can be on screen).
const store = {
  uid: null,
  data: { dancers: [], studios: [] },
  loaded: false,
  listeners: new Set(),
  unsubDoc: null,
  unsubAuth: null,
};

const emit = () => store.listeners.forEach((l) => l());

function ensureSubscribed() {
  if (store.unsubAuth) return;
  store.unsubAuth = onAuthStateChanged(auth, (user) => {
    if (store.unsubDoc) {
      store.unsubDoc();
      store.unsubDoc = null;
    }
    store.uid = user ? user.uid : null;
    if (!user) {
      store.data = { dancers: [], studios: [] };
      store.loaded = true;
      emit();
      return;
    }
    store.loaded = false;
    emit();
    store.unsubDoc = onSnapshot(
      doc(db, 'userFollows', user.uid),
      (snap) => {
        const d = snap.data() || {};
        store.data = { dancers: d.dancers || [], studios: d.studios || [] };
        store.loaded = true;
        emit();
      },
      (err) => {
        console.error('Follow list listener error:', err);
        store.loaded = true;
        emit();
      }
    );
  });
}

/**
 * Follow state for the signed-in user.
 * follow()/unfollow() resolve to { ok, reason? } — reason 'login' when signed out, 'limit' at MAX_FOLLOWS.
 */
export function useFollows() {
  const [, setTick] = useState(0);

  useEffect(() => {
    ensureSubscribed();
    const listener = () => setTick((t) => t + 1);
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, []);

  const isFollowing = useCallback(
    (type, name) => (type === 'studio' ? store.data.studios : store.data.dancers).includes(name),
    []
  );

  const change = useCallback(async (type, name, add) => {
    if (!store.uid) return { ok: false, reason: 'login' };
    const field = type === 'studio' ? 'studios' : 'dancers';
    if (add && store.data.dancers.length + store.data.studios.length >= MAX_FOLLOWS) {
      return { ok: false, reason: 'limit' };
    }
    // Optimistic update so the button flips instantly
    const prev = store.data;
    store.data = {
      ...prev,
      [field]: add ? [...prev[field], name] : prev[field].filter((n) => n !== name),
    };
    emit();
    try {
      await setDoc(
        doc(db, 'userFollows', store.uid),
        { [field]: add ? arrayUnion(name) : arrayRemove(name), updatedAt: serverTimestamp() },
        { merge: true }
      );
      return { ok: true };
    } catch (err) {
      console.error('Follow update failed:', err);
      store.data = prev;
      emit();
      return { ok: false, reason: 'error' };
    }
  }, []);

  return {
    dancers: store.data.dancers,
    studios: store.data.studios,
    loaded: store.loaded,
    signedIn: !!store.uid,
    isFollowing,
    follow: (type, name) => change(type, name, true),
    unfollow: (type, name) => change(type, name, false),
  };
}
